/**
 * Calculs géographiques de la console — purs, testés.
 *
 * Deux usages :
 *  • la carte des livreurs (une position trop ancienne ne doit pas passer
 *    pour une position vraie) ;
 *  • la preuve de livraison (l'écart entre l'endroit où le livreur a validé
 *    et le point de livraison est un signal de litige).
 */

/** Au-delà, une position n'est plus « en direct » : le livreur a pu bouger. */
export const FRAICHEUR_POSITION_MS = 10 * 60_000

/** Au-delà, l'écart validation ↔ point de livraison est un signal de litige. */
export const SEUIL_LITIGE_M = 300

const RAYON_TERRE_M = 6_371_000

/** Distance orthodromique (haversine), en mètres. */
export function distanceMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(lat2 - lat1)
  const dLng = rad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * RAYON_TERRE_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Une coordonnée est-elle exploitable ? `0,0` (golfe de Guinée) est un défaut d'appareil, pas une position. */
export function coordonneeValide(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null) return false
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false
  return !(lat === 0 && lng === 0)
}

export type EtatPosition =
  | { etat: 'recente'; ageMs: number }
  | { etat: 'ancienne'; ageMs: number }
  | { etat: 'aucune' }

/**
 * Fraîcheur de la position d'un livreur.
 *
 * Une position sans horodatage est traitée comme ancienne : on ne sait pas
 * de quand elle date, on ne peut donc pas la présenter comme actuelle.
 */
export function etatPosition(
  l: { lat: number | null; lng: number | null; last_seen: string | null },
  maintenant: number = Date.now(),
): EtatPosition {
  if (!coordonneeValide(l.lat, l.lng)) return { etat: 'aucune' }
  const t = l.last_seen ? new Date(l.last_seen).getTime() : NaN
  if (!Number.isFinite(t)) return { etat: 'ancienne', ageMs: Number.POSITIVE_INFINITY }
  const ageMs = Math.max(0, maintenant - t)
  return ageMs <= FRAICHEUR_POSITION_MS ? { etat: 'recente', ageMs } : { etat: 'ancienne', ageMs }
}

/** « il y a 4 min », « il y a 2 h », « il y a 3 j », « date inconnue ». */
export function depuis(ageMs: number): string {
  if (!Number.isFinite(ageMs)) return 'date inconnue'
  const min = Math.floor(ageMs / 60_000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.floor(h / 24)} j`
}

export type EcartLivraison =
  | { etat: 'mesure'; metres: number; litige: boolean }
  | { etat: 'sans_position_validation' }
  | { etat: 'sans_point_livraison' }

/**
 * Écart entre la position de validation (`proof_lat/lng`) et le point de
 * livraison (`dropoff_lat/lng`).
 *
 * ⚠️ Les deux absences se disent DIFFÉREMMENT : « le livreur n'a pas transmis
 * sa position » n'est pas la même chose que « la course n'a jamais eu de point
 * géolocalisé » (cas courant : la prise d'appel ne saisit qu'un quartier). Les
 * confondre ferait accuser un livreur d'un manque qui vient du dispatch.
 */
export function ecartLivraison(c: {
  proof_lat: number | null
  proof_lng: number | null
  dropoff_lat: number | null
  dropoff_lng: number | null
}): EcartLivraison {
  if (!coordonneeValide(c.dropoff_lat, c.dropoff_lng)) return { etat: 'sans_point_livraison' }
  if (!coordonneeValide(c.proof_lat, c.proof_lng)) return { etat: 'sans_position_validation' }
  const metres = Math.round(
    distanceMetres(c.proof_lat as number, c.proof_lng as number, c.dropoff_lat as number, c.dropoff_lng as number),
  )
  return { etat: 'mesure', metres, litige: metres > SEUIL_LITIGE_M }
}

/** « 85 m », « 1,4 km ». */
export function formaterDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`
}

/** Centre de Bamako : repère par défaut de la carte, des Plus Codes courts et de la saisie d'adresse. */
export const BAMAKO: readonly [number, number] = [12.6392, -8.0029]

/**
 * Rectangle englobant LARGE du Mali — le même que celui de la base (`enregistrer_position_destinataire`,
 * migration 0030) : un point hors de ce rectangle n'est pas une adresse de livraison à Bamako.
 */
export function dansBoiteMali(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= 10.0 && lat <= 25.5 && lng >= -12.5 && lng <= 4.5
}
